//* 组件库
import { $loading } from "various-ui";
import type { UiTypes } from "various-ui";
//* 公共组件
import type { SearchDiagram } from "@/components/visualizations";
//* 工具库
import { Formats } from "@/assets/js";
//* 组件属性
import type { ModuleSelector } from "../index";

export const useComposable = (mode: string) => {
    //* 插件
    const route = useRoute();

    //* 响应式变量
    const refs = {
        data: ref<any[]>([]),
        page: ref<number>(1),
        sort: reactive({ key: "", value: "" }),
        exist: ref<Boolean>(true),
        count: ref<number>(0),
        keyword: ref<string>(""),
        selector: reactive<ModuleSelector[]>([]),
        selector2: ref<UiTypes.candidate[]>([]),
        // * 突变关键词
        keyword2: ref<string>(""),
        gene: ref(""),
    };

    //* 节点
    const nodes = {
        table: ref<InstanceType<typeof SearchDiagram>>(),
        container: ref<HTMLDivElement>(),
    };

    //* 响应事件
    const ons = {
        diagram: {
            //* 排序
            sort: (key?: string, value?: string) => {
                //* 数据更新
                refs.sort.key = key || "";
                refs.sort.value = value || "";
                //* 数据获取
                methods.data(1);
            },

            //* 页面跳转
            click: (data: any) => {
                switch (mode) {
                    case "gene":
                        return open(
                            routerResolve({ name: "Gene", params: { id: data.id }, query: { keyword: refs.keyword.value } }).href as string,
                            "_blank"
                        );
                    case "model":
                        return open(routerResolve({ name: "Model", params: { id: data.id } }).href as string, "_blank");
                    case "disease":
                        return open(routerResolve({ name: "Disease", params: { id: data.id } }).href as string, "_blank");
                }
            },
        },
    };

    //* 函数列表
    const methods = {
        //* 初始化函数
        init: () => {
            //* 开启Loading, 如果在3毫秒内关闭则不会出现
            $loading.open({ delay: 300, message: "" });
            //* 接口请求
            const keyword = Formats.query(route.query.keyword)?.trim();
            api.frontend("/api/search", {
                data: { mode: mode, keyword: keyword },
                method: "POST",
                finally: () => $loading.close(),
                success: (response) => {
                    //* 数据初始化
                    refs.sort.key = "";
                    refs.page.value = 1;
                    refs.sort.value = "";
                    refs.data.value = response.data || [];
                    refs.count.value = response.count || 0;
                    refs.exist.value = refs.data.value.length > 0;
                    refs.keyword.value = keyword;
                    refs.keyword2.value = "";
                    refs.gene.value = "";
                    refs.selector.splice(0, refs.selector.length);
                    response.selector?.forEach((value: any) => {
                        refs.selector.push({ key: value.key, name: value.name, result: [], options: value.options, selector: false });
                    });
                    refs.selector2.value = response.selector2;

                    //* 可视化图表清空状态
                    nodes.table.value?.clear();
                },
            });
        },

        //* 数据
        data: (number?: number) => {
            //* 开启Loading, 如果在3毫秒内关闭则不会出现
            $loading.open({ delay: 300, message: "" });
            const selector: { key: string; value: string }[] = [];
            if (refs.gene.value) {
                selector.push({ key: "gene_name", value: refs.gene.value });
            }
            //* 接口数据初始化
            //* 筛选数据添加
            refs.selector.forEach((data) => {
                data.options.forEach((opt) => {
                    data.result.includes(opt.value) && selector.push({ key: opt.field, value: opt.value });
                });
            });

            //* 接口请求
            refs.page.value = number || refs.page.value || 1;
            api.frontend("/api/search", {
                finally: () => $loading.close(),
                method: "POST",
                data: {
                    mode: mode,
                    page: refs.page.value,
                    keyword: refs.keyword.value,
                    sortKey: refs.sort.key,
                    selector: selector,
                    sortValue: refs.sort.value,
                    mutation_pos: refs.keyword2.value,
                },
                success: (response) => {
                    //* 数据更新
                    refs.data.value = response.data || [];
                    refs.count.value = response.count || 0;
                    //* 回到容器顶部
                    scrollTo({ top: nodes.container.value?.offsetTop || 0, behavior: "smooth" });
                },
            });
        },

        //* 单选
        selector: (data: ModuleSelector) => {
            //* 选项数据更新
            const selector = refs.selector.find((value) => value.name == data.name);
            if (selector) {
                if (selector.result.length == selector.options.length) selector.selector = true;
                else {
                    selector.selector = false;
                }
            }

            //* 数据获取
            methods.data(1);
        },

        //* 全选
        selectors: (data: ModuleSelector) => {
            //* 选项数据更新
            const selector = refs.selector.find((value) => value.name == data.name);
            if (selector) {
                if (!selector.selector) selector.result.splice(0, selector.result.length);
                else {
                    selector.options.forEach((value) => {
                        if (!selector.result.includes(value.value)) {
                            selector.result.push(value.value);
                        }
                    });
                }
            }

            //* 数据获取
            methods.data(1);
        },
    };

    //* 监听器
    const watchs = {
        stop: watch(
            () => route.query,
            () => {
                methods.init();
            }
        ),
    };

    return { ons, refs, nodes, watchs, methods };
};
